import {
  AccountIdentifier,
  SubAccount as ICPSubaccount,
  IcpLedgerCanister,
} from '@icp-sdk/canisters/ledger/icp'
import { IcrcLedgerCanister } from '@icp-sdk/canisters/ledger/icrc'
import { HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import OdinClient from '../../clients/OdinClient'
import { ODINBTC_ID } from '../../constants/tokenIds'
import { Chain } from '../../interfaces/common'
import { SendParams } from '../../interfaces/walletActions'
import { validatePrincipal } from '../validationHelper'
import getTokenActor from './getTokenActor'

interface SendTokenParams {
  sendParams: SendParams
  agent: HttpAgent
}

const numberToUint8Array = (number: number): Uint8Array => {
  const result = new Uint8Array(8)

  for (let i = 8; i > 0; i--) {
    result[i - 1] = number % 256
    number = Math.floor(number / 256)
  }

  return result
}

const sendToken = async ({
  agent,
  sendParams: { standard, tokenCanisterId, receiver, amount, memo, tokenId, chain },
}: SendTokenParams) => {
  const hasMemo = typeof memo === 'number'

  if (chain === Chain.ODIN) {
    if (!tokenId) {
      throw 'tokenId is missing to send Odin tokens'
    }

    const odinClient = new OdinClient(agent)
    const response = await odinClient.transferToken(
      receiver.address,
      tokenId === ODINBTC_ID ? 'btc' : tokenId,
      amount
    )

    if ('ok' in response) {
      return Number(0)
    }

    if ('err' in response) {
      return Promise.reject(response.err)
    }
  } else if (tokenCanisterId) {
    switch (standard) {
      case 'ICP':
        const icpLedger = IcpLedgerCanister.create({
          agent,
          canisterId: Principal.fromText(tokenCanisterId),
        })

        const isPrincipal = validatePrincipal(receiver.address)

        const subAccountUnwrapped = receiver.subaccount
          ? ICPSubaccount.fromBytes(Uint8Array.from(receiver.subaccount))
          : undefined

        const subAccount =
          subAccountUnwrapped instanceof Error ? undefined : (subAccountUnwrapped as ICPSubaccount)

        const accountIdentifier = isPrincipal
          ? AccountIdentifier.fromPrincipal({
              principal: Principal.fromText(receiver.address),
              subAccount,
            })
          : AccountIdentifier.fromHex(receiver.address)

        const response = await icpLedger.transfer({
          to: accountIdentifier,
          amount: BigInt(amount),
          memo: hasMemo ? BigInt(memo) : undefined,
        })

        return Number(response)
      case 'ICRC1':
      case 'ICRC2':
      case 'ICRC3':
        const icrcLedger = IcrcLedgerCanister.create({
          agent,
          canisterId: Principal.fromText(tokenCanisterId),
        })

        const icrcTransfer = await icrcLedger.transfer({
          to: {
            owner: Principal.fromText(receiver.address),
            // Candid blobs are Uint8Array in @icp-sdk/canisters; this app carries
            // subaccounts around as number[].
            subaccount: receiver.subaccount ? [Uint8Array.from(receiver.subaccount)] : [],
          },
          amount: BigInt(amount),
          memo: hasMemo ? numberToUint8Array(memo) : undefined,
        })

        return Number(icrcTransfer)
      default:
        const actor = getTokenActor(tokenCanisterId, standard, agent)

        if (actor.dip20) {
          const response = await actor.dip20.transfer(
            Principal.from(receiver.address),
            BigInt(amount)
          )

          if ('Ok' in response) {
            return Number(response.Ok)
          }

          if ('Err' in response) {
            return Promise.reject(Object.keys(response.Err)[0])
          }

          return Promise.reject('Unknown DIP20 state')
        }

        if (actor.drc20) {
          const response = await actor.drc20.drc20_transfer(
            receiver.address,
            BigInt(amount),
            [],
            [],
            hasMemo ? [numberToUint8Array(memo)] : []
          )

          if ('ok' in response) {
            return Number(response.ok)
          }

          if ('err' in response) {
            return Promise.reject(response.err.message)
          }

          return Promise.reject('Unknown DRC20 response')
        }

        if (actor.xtc) {
          const response = await actor.xtc.transfer(
            Principal.from(receiver.address),
            BigInt(amount)
          )

          if ('Ok' in response) {
            return Number(response.Ok)
          }

          if ('Err' in response) {
            return Promise.reject(Object.keys(response.Err)[0])
          }

          return Promise.reject('Unknown XTC error')
        }

        return Promise.reject(`Unsupported token ${standard}`)
    }
  } else {
    throw 'tokenCanisterId is missing'
  }
}

export default sendToken
